import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { client } from '@frontend/features/common/services';
import { countries } from '@frontend/features/common/constants/countries';
import { HotelLocationPicker } from '@frontend/features/hotels/HotelLocationPicker';
import type { HotelLocation } from '@frontend/features/hotels/searchModel';
import {
  effectivePolicyRoutes,
  CountryPolicy,
  DestinationPolicy,
  PolicyMode,
  PolicyRoute,
  PolicyRouteOption,
  SearchPolicy,
  policyPayload,
  policyValid,
  routeKey,
  recoverInactiveRoutes,
} from './policy';

import { useProviderNames } from './useProviderNames';
import { providerLabel } from './providerNames';
import { PolicyRouteEditor } from './PolicyRouteEditor';

type Scope = 'default' | 'country' | 'destination';
type Editor = {
  agencyId: number;
  scope: Scope;
  original?: string;
  country: string;
  location: HotelLocation | null;
  mode: DestinationPolicy['mode'];
  routes: PolicyRoute[];
  excluded_routes: PolicyRoute[];
  remove?: boolean;
};
type Preview = { country_code: string | null; inherited: PolicyRouteOption[]; effective: PolicyRouteOption[] };
const countryOptions = countries.filter((c, i, list) => list.findIndex((x) => x.code === c.code) === i);

export function HotelSearchPolicy({ agencyId }: { agencyId: number }) {
  const { t, i18n } = useTranslation();
  const cache = useQueryClient();
  const suppliers = useProviderNames();
  const path = `agencies/${agencyId}/hotels/search-policy`,
    key = ['hotel-search-policy', agencyId];
  const policy = useQuery(key, async () => (await client.get<{ data: SearchPolicy }>(path)).data.data, {
    enabled: Number.isSafeInteger(agencyId) && agencyId > 0,
    retry: false,
  });
  const options = useQuery(
    [...key, 'routes'],
    async () =>
      (
        await client.get<{ data: PolicyRouteOption[]; unsupported_accounts: { display_name: string }[] }>(
          `${path}/routes`,
        )
      ).data,
    { enabled: !!policy.data, retry: false },
  );
  const [base, setBase] = useState<SearchPolicy | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [recovery, setRecovery] = useState<
    (ReturnType<typeof recoverInactiveRoutes> & { agencyId: number; inventory: string }) | null
  >(null);
  const [reason, setReason] = useState('');
  const [conflict, setConflict] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [query, setQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [testing, setTesting] = useState(false);
  const [testLocation, setTestLocation] = useState<HotelLocation | null>(null);
  const [testCountry, setTestCountry] = useState('');
  useEffect(() => {
    setEditor(null);
    setRecovery(null);
    setBase(null);
    setTesting(false);
    setTestLocation(null);
    setTestCountry('');
  }, [agencyId]);
  const countryName = (code: string) =>
    new Intl.DisplayNames([i18n?.language || 'es'], { type: 'region' }).of(code) ?? code;
  const routeLabel = (r: PolicyRoute) => {
    const found = options.data?.data?.find((o) => routeKey(o) === routeKey(r));
    return providerLabel(
      found ?? { ...r, display_name: r.connector_account_id },
      suppliers.names,
      t('hotelPolicy.unknownProvider'),
    );
  };
  const ruleId =
    editor?.scope === 'country'
      ? editor.country
      : editor?.location?.type === 'destination'
        ? editor.location.placeId
        : '';
  const duplicate = !!(
    editor &&
    base &&
    editor.scope !== 'default' &&
    ruleId !== editor.original &&
    (editor.scope === 'country'
      ? base.country_overrides?.some((r) => r.country_code === ruleId)
      : base.destination_overrides.some((r) => r.place_id === ruleId))
  );
  const inventoryReady =
    !options.isLoading && !options.isFetching && !options.isError && Array.isArray(options.data?.data);
  const inventory = JSON.stringify((options.data?.data ?? []).map(routeKey).sort());
  const repair = policy.data && inventoryReady ? recoverInactiveRoutes(policy.data, options.data!.data) : null;
  const recoveryValid =
    !!recovery &&
    recovery.agencyId === agencyId &&
    inventoryReady &&
    recovery.inventory === inventory &&
    policyValid(recovery.policy, reason);
  const draft =
    recovery?.policy ??
    (base && editor
      ? (() => {
          if (editor.scope === 'default')
            return { ...base, default_mode: editor.mode as PolicyMode, default_routes: editor.routes };
          const rule = { mode: editor.mode, routes: editor.routes, excluded_routes: editor.excluded_routes };
          if (editor.scope === 'country')
            return {
              ...base,
              country_overrides: [
                ...(base.country_overrides ?? []).filter((r) => r.country_code !== editor.original),
                ...(editor.remove ? [] : [{ ...rule, country_code: editor.country }]),
              ],
            };
          return {
            ...base,
            destination_overrides: [
              ...base.destination_overrides.filter((r) => r.place_id !== editor.original),
              ...(editor.remove || editor.location?.type !== 'destination'
                ? []
                : [
                    {
                      ...rule,
                      source: 'ubikeishon' as const,
                      place_id: editor.location.placeId,
                      display_name: editor.location.label.trim().slice(0, 200),
                    },
                  ]),
            ],
          };
        })()
      : null);
  const valid =
    !!draft &&
    !!editor &&
    editor.agencyId === agencyId &&
    !duplicate &&
    !resolving &&
    (editor.scope === 'default' || !!ruleId) &&
    policyValid(draft, reason);
  const previewPolicy = testing ? policy.data : draft;
  const previewTarget = testing
    ? testLocation?.type === 'destination'
      ? { place_id: testLocation.placeId }
      : testCountry
        ? { country_code: testCountry }
        : {}
    : editor?.scope === 'destination' && editor.location?.type === 'destination'
      ? { place_id: editor.location.placeId }
      : editor?.scope === 'country'
        ? { country_code: editor.country }
        : {};
  const previewEnabled =
    !!previewPolicy &&
    (testing
      ? !!testCountry || testLocation?.type === 'destination'
      : !!editor && (editor.scope === 'default' || !!ruleId)) &&
    !duplicate &&
    !resolving;
  const preview = useQuery(
    [...key, 'preview', previewPolicy, previewTarget],
    async () =>
      (
        await client.post<Preview>(`${path}/preview`, {
          policy: policyPayload(previewPolicy!, 'preview'),
          ...previewTarget,
        })
      ).data,
    { enabled: previewEnabled, retry: false, staleTime: 0 },
  );
  const save = useMutation(
    async () => {
      if (!(recovery ? recoveryValid : valid) || conflict || !draft) throw new Error('Invalid policy');
      await client.put(path, policyPayload(draft, reason));
    },
    {
      onSuccess: () => {
        setEditor(null);
        setRecovery(null);
        setBase(null);
        cache.invalidateQueries(key);
      },
      onError: (error: any) => {
        if (error?.response?.status === 409) setConflict(true);
      },
    },
  );
  const busy = save.isLoading || conflict;
  const open = (scope: Scope, row?: CountryPolicy | DestinationPolicy, remove = false) => {
    if (!policy.data) return;
    setBase(JSON.parse(JSON.stringify(policy.data)));
    setReason('');
    setConflict(false);
    save.reset();
    setResolving(false);
    setEditor({
      agencyId,
      scope,
      original: row ? ('country_code' in row ? row.country_code : row.place_id) : undefined,
      country: row && 'country_code' in row ? row.country_code : '',
      location:
        row && 'place_id' in row
          ? { type: 'destination', placeId: row.place_id, label: row.display_name ?? row.place_id }
          : null,
      mode: row?.mode ?? (scope === 'default' ? policy.data.default_mode : 'extend'),
      routes: row?.routes ?? (scope === 'default' ? policy.data.default_routes : []),
      excluded_routes: row?.excluded_routes ?? [],
      remove,
    });
  };
  const tags = (routes: PolicyRoute[]) => (
    <Stack direction="row" gap={1} flexWrap="wrap">
      {routes.length ? (
        routes.map((r) => (
          <Chip
            size="small"
            key={routeKey(r)}
            label={routeLabel(r)}
            sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
          />
        ))
      ) : (
        <Typography variant="body2">{t('hotelPolicy.noneEffective')}</Typography>
      )}
    </Stack>
  );
  const rules = policy.data
    ? [
        ...(policy.data.country_overrides ?? []).map((row) => ({
          scope: 'country' as const,
          id: row.country_code,
          name: countryName(row.country_code),
          row,
        })),
        ...policy.data.destination_overrides.map((row) => ({
          scope: 'destination' as const,
          id: row.place_id,
          name: row.display_name ?? row.place_id,
          row,
        })),
      ].filter(
        (r) =>
          (scopeFilter === 'all' || scopeFilter === r.scope) &&
          `${r.name} ${r.id}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()),
      )
    : [];
  const previewBox = (
    <Stack spacing={1}>
      {preview.isFetching && <Typography>{t('hotelPolicy.loading')}</Typography>}
      {preview.isError && (
        <Alert severity="error">
          {t('hotelPolicy.previewError')}
          <Button onClick={() => preview.refetch()}>{t('hotelPolicy.retry')}</Button>
          <Typography variant="caption" display="block">
            {t('hotelPolicy.errorStatus', {
              status: (preview.error as any)?.response?.status ?? t('hotelPolicy.networkError'),
            })}
          </Typography>
        </Alert>
      )}
      {!preview.isFetching && !preview.isError && previewEnabled && preview.data && (
        <>
          {preview.data.country_code && (
            <Typography variant="body2">
              {t('hotelPolicy.country')}: {countryName(preview.data.country_code)}
            </Typography>
          )}
          {editor?.scope !== 'default' && !testing && (
            <>
              <Typography variant="subtitle2">{t('hotelPolicy.inheritedFrom')}</Typography>
              {tags(preview.data.inherited)}
            </>
          )}
          <Typography variant="subtitle2">{t('hotelPolicy.effective')}</Typography>
          {tags(preview.data.effective)}
        </>
      )}
    </Stack>
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" gap={1} alignItems="center">
        <Typography variant="h6">{t('hotelPolicy.title')}</Typography>{' '}
        <Button
          onClick={() => {
            setTestCountry('');
            setTestLocation(null);
            setResolving(false);
            setTesting(true);
          }}
        >
          {t('hotelPolicy.test')}
        </Button>
      </Stack>
      <Typography sx={{ my: 2 }}>{t('hotelPolicy.hierarchy')}</Typography>
      {policy.isLoading && <Typography>{t('hotelPolicy.loading')}</Typography>}
      {policy.isError && (
        <Alert severity="error">
          {t('hotelPolicy.loadError')} <Button onClick={() => policy.refetch()}>{t('hotelPolicy.reload')}</Button>
        </Alert>
      )}
      {options.isError && (
        <Alert severity="error">
          {t('hotelPolicy.routesError')} <Button onClick={() => options.refetch()}>{t('hotelPolicy.retry')}</Button>
        </Alert>
      )}
      {suppliers.isError && (
        <Alert severity="info">
          {t('hotelPolicy.namesUnavailable')}{' '}
          <Button onClick={() => suppliers.refetch()}>{t('hotelPolicy.retry')}</Button>
        </Alert>
      )}
      {!!options.data?.unsupported_accounts?.length && (
        <Alert severity="warning">
          {t('hotelPolicy.unsupportedAccounts')}{' '}
          {options.data.unsupported_accounts.map((a) => a.display_name).join(', ')}
        </Alert>
      )}
      {!!repair?.affected.length && (
        <Alert
          severity="warning"
          sx={{ my: 2 }}
          action={
            <Button
              onClick={() => {
                setEditor(null);
                setRecovery({ ...repair, agencyId, inventory });
                setReason('');
                setConflict(false);
                save.reset();
              }}
            >
              {t('hotelPolicy.repair')}
            </Button>
          }
        >
          {t('hotelPolicy.inactiveRules')}
        </Alert>
      )}
      {policy.data && (
        <Stack spacing={2}>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('hotelPolicy.default')}</Typography>
              <Button onClick={() => open('default')}>{t('hotelPolicy.edit')}</Button>
            </Stack>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t(`hotelPolicy.${policy.data.default_mode}`)}
            </Typography>
            {options.isLoading ? (
              <Typography>{t('hotelPolicy.loading')}</Typography>
            ) : (
              tags(effectivePolicyRoutes(policy.data, options.data?.data ?? []))
            )}
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="space-between">
            <Typography variant="h6">{t('hotelPolicy.exceptions')}</Typography>
            <Button onClick={() => open('country')}>{t('hotelPolicy.addRule')}</Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <TextField
              fullWidth
              label={t('hotelPolicy.findRule')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <TextField
              select
              label={t('hotelPolicy.scopeType')}
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {['all', 'country', 'destination'].map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`hotelPolicy.${v}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {!rules.length && <Typography>{t('hotelPolicy.noExceptions')}</Typography>}
          {rules.map(({ scope, id, name, row }) => (
            <Box key={`${scope}:${id}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" justifyContent="space-between">
                <Typography fontWeight={600}>
                  {name} · {t(`hotelPolicy.${scope === 'country' ? 'allDestinations' : 'destination'}`)}
                </Typography>
                <Stack direction="row">
                  <Button onClick={() => open(scope, row)}>{t('hotelPolicy.edit')}</Button>
                  <Button onClick={() => open(scope, row, true)}>{t('hotelPolicy.remove')}</Button>
                </Stack>
              </Stack>
              <Typography variant="body2">{t(`hotelPolicy.${row.mode}`)}</Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                {(row.mode === 'extend' || row.mode === 'allowlist') &&
                  row.routes.map((r) => (
                    <Chip
                      size="small"
                      key={routeKey(r)}
                      label={`${row.mode === 'extend' ? t('hotelPolicy.added') + ': ' : ''}${routeLabel(r)}`}
                      sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                    />
                  ))}
                {row.mode === 'extend' &&
                  row.excluded_routes?.map((r) => (
                    <Chip
                      size="small"
                      key={routeKey(r)}
                      label={`${t('hotelPolicy.excluded')}: ${routeLabel(r)}`}
                      sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                    />
                  ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
      <Dialog
        open={!!editor && editor.agencyId === agencyId}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!save.isLoading) setEditor(null);
        }}
      >
        <DialogTitle>{t(editor?.remove ? 'hotelPolicy.removeRule' : 'hotelPolicy.edit')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {editor && (
              <>
                {editor.scope !== 'default' && (
                  <TextField
                    select
                    label={t('hotelPolicy.scopeType')}
                    disabled={busy || !!editor.original}
                    value={editor.scope}
                    onChange={(e) =>
                      setEditor({ ...editor, scope: e.target.value as Scope, country: '', location: null })
                    }
                  >
                    {['country', 'destination'].map((v) => (
                      <MenuItem key={v} value={v}>
                        {t(`hotelPolicy.${v}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {editor.scope === 'country' && (
                  <Autocomplete
                    options={countryOptions}
                    value={countryOptions.find((c) => c.code === editor.country) ?? null}
                    disabled={busy || !!editor.original}
                    getOptionLabel={(c) => countryName(c.code)}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    onChange={(_, c) => setEditor({ ...editor, country: c?.code ?? '' })}
                    renderInput={(params) => <TextField {...params} label={t('hotelPolicy.country')} />}
                  />
                )}
                {editor.scope === 'destination' &&
                  (editor.original ? (
                    <Typography>{editor.location?.label}</Typography>
                  ) : (
                    <HotelLocationPicker
                      value={editor.location}
                      onChange={(location) => setEditor({ ...editor, location })}
                      onResolving={setResolving}
                    />
                  ))}
                {editor.location?.type === 'point' && (
                  <Alert severity="info">{t('hotelPolicy.chooseDestination')}</Alert>
                )}
                {duplicate && <Alert severity="error">{t('hotelPolicy.duplicate')}</Alert>}
                {editor.remove ? (
                  <Alert severity="warning">{t('hotelPolicy.removeHelp')}</Alert>
                ) : (
                  <>
                    <TextField
                      select
                      label={t('hotelPolicy.behavior')}
                      value={editor.mode}
                      disabled={busy}
                      onChange={(e) => setEditor({ ...editor, mode: e.target.value as Editor['mode'] })}
                    >
                      {[
                        ...(editor.scope === 'default' ? [] : ['inherit', 'extend']),
                        'all_enabled',
                        'allowlist',
                        'disabled',
                      ].map((v) => (
                        <MenuItem key={v} value={v}>
                          {t(`hotelPolicy.${v}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {editor.mode === 'all_enabled' && <Alert severity="info">{t('hotelPolicy.includesFuture')}</Alert>}
                    {(editor.mode === 'allowlist' || editor.mode === 'extend') && (
                      <PolicyRouteEditor
                        options={options.data?.data ?? []}
                        inherited={preview.data?.inherited ?? []}
                        selected={editor.routes}
                        excluded={editor.excluded_routes}
                        extend={editor.mode === 'extend'}
                        names={suppliers.names}
                        disabled={busy || options.isLoading || options.isError || preview.isFetching}
                        change={(routes, excluded_routes) => setEditor({ ...editor, routes, excluded_routes })}
                      />
                    )}
                  </>
                )}
                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>{previewBox}</Box>
                <TextField
                  label={t('hotelPolicy.reason')}
                  value={reason}
                  disabled={busy}
                  inputProps={{ maxLength: 1000 }}
                  onChange={(e) => setReason(e.target.value)}
                />
                {save.isError && (
                  <Alert severity="error">{t(conflict ? 'hotelPolicy.conflict' : 'hotelPolicy.saveError')}</Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setEditor(null)}>
            {t('hotelPolicy.close')}
          </Button>
          {conflict ? (
            <Button
              onClick={() => {
                setEditor(null);
                policy.refetch();
                options.refetch();
              }}
            >
              {t('hotelPolicy.reload')}
            </Button>
          ) : (
            <Button
              disabled={
                !valid ||
                save.isLoading ||
                options.isLoading ||
                options.isError ||
                !preview.data ||
                preview.isFetching ||
                preview.isError
              }
              onClick={() => save.mutate()}
            >
              {t('hotelPolicy.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!recovery && recovery.agencyId === agencyId}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!save.isLoading) setRecovery(null);
        }}
      >
        <DialogTitle>{t('hotelPolicy.repair')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">{t('hotelPolicy.repairHelp')}</Alert>
            {recovery?.affected.map((row, index) => (
              <Box key={index}>
                <Typography variant="subtitle2">
                  {t(`hotelPolicy.${row.scope}`)}
                  {row.name ? ` · ${row.scope === 'country' ? countryName(row.name) : row.name}` : ''}
                </Typography>
                {tags(row.removed)}
              </Box>
            ))}
            {recovery && (!inventoryReady || recovery.inventory !== inventory) && (
              <Alert severity="warning">{t('hotelPolicy.repairInventoryChanged')}</Alert>
            )}
            <TextField
              label={t('hotelPolicy.reason')}
              value={reason}
              disabled={busy}
              inputProps={{ maxLength: 1000 }}
              onChange={(e) => setReason(e.target.value)}
            />
            {(save.isError || conflict) && (
              <Alert severity="error">{t(conflict ? 'hotelPolicy.conflict' : 'hotelPolicy.saveError')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setRecovery(null)}>
            {t('hotelPolicy.close')}
          </Button>
          {conflict ? (
            <Button
              onClick={() => {
                setRecovery(null);
                policy.refetch();
                options.refetch();
              }}
            >
              {t('hotelPolicy.reload')}
            </Button>
          ) : (
            <Button disabled={!recoveryValid || save.isLoading} onClick={() => save.mutate()}>
              {t('hotelPolicy.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={testing} fullWidth maxWidth="sm" onClose={() => setTesting(false)}>
        <DialogTitle>{t('hotelPolicy.test')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              options={countryOptions}
              value={countryOptions.find((c) => c.code === testCountry) ?? null}
              getOptionLabel={(c) => countryName(c.code)}
              onChange={(_, c) => {
                setTestCountry(c?.code ?? '');
                setTestLocation(null);
              }}
              renderInput={(params) => <TextField {...params} label={t('hotelPolicy.country')} />}
            />
            <HotelLocationPicker
              key={testCountry}
              value={testLocation}
              onChange={(location) => {
                setTestLocation(location);
                if (location) setTestCountry('');
              }}
              onResolving={setResolving}
            />
            {testLocation?.type === 'point' && <Alert severity="info">{t('hotelPolicy.chooseDestination')}</Alert>}
            {previewBox}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTesting(false)}>{t('hotelPolicy.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
